import type { HTMLAttributes, ReactNode, Ref } from "react";
import { FaWallet } from "react-icons/fa6";

import { cn } from "../../utils";

export type WalletIconSize = "xs" | "sm" | "md" | "lg" | "xl";
export type WalletIconVariant =
  | "metamask"
  | "rabby"
  | "frame"
  | "rainbow"
  | "brave"
  | "braveevm"
  | "phantom"
  | "phantomevm"
  | "coinbase";

export interface WalletIconProps extends HTMLAttributes<HTMLImageElement> {
  ref?: Ref<HTMLImageElement>;
  size?: WalletIconSize;
  variant: WalletIconVariant;
  fallback?: ReactNode;
}

const walletSrcMap: Record<WalletIconVariant, string> = {
  metamask: new URL("../assets/wallets/metamask.svg", import.meta.url).href,
  rabby: new URL("../assets/wallets/rabby.svg", import.meta.url).href,
  frame: new URL("../assets/wallets/frame.png", import.meta.url).href,
  rainbow: new URL("../assets/wallets/rainbow.svg", import.meta.url).href,
  brave: new URL("../assets/wallets/brave.svg", import.meta.url).href,
  braveevm: new URL("../assets/wallets/brave.svg", import.meta.url).href,
  phantom: new URL("../assets/wallets/phantom.png", import.meta.url).href,
  phantomevm: new URL("../assets/wallets/phantom.png", import.meta.url).href,
  coinbase: new URL("../assets/wallets/coinbase.jpeg", import.meta.url).href,
};

export const WalletIcon = ({
  size = "md",
  variant,
  fallback,
  className,
  onClick,
  ref,
  ...props
}: WalletIconProps) => {
  const src = walletSrcMap[variant];

  const sizeClassName = cn({
    "ui:size-3": size === "xs",
    "ui:size-4": size === "sm",
    "ui:size-5": size === "md",
    "ui:size-6": size === "lg",
    "ui:size-7": size === "xl",
  });

  if (!src)
    return fallback ?? <FaWallet className={cn(sizeClassName, className)} />;

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions -- The img element is used here for displaying the wallet icon and needs to handle click events for interaction purposes.
    <img
      ref={ref}
      src={src}
      alt={variant}
      className={cn(
        {
          "ui:cursor-pointer": Boolean(onClick),
          "ui:rounded-full": variant === "coinbase",
        },
        sizeClassName,
        className,
      )}
      onClick={onClick}
      {...props}
    />
  );
};
